<template>
    <Dialog :visible="showDialog" class="action-popup" :modal="true" :draggable="false">
        <template #container>
            <div class="dialog-header-danger">
                Delete Account
            </div>
            <div class="dialog-content align-items-center">
                
                <!-- Account CAN be deleted -->
                <div v-if="accountCanBeDeleted" class="w-full flex flex-column align-items-center">
                    <div class="w-full flex justify-content-center align-items-center mb-3">
                        <SvgWarning className="popup-warning-svg" color="var(--popup-warning-color)"/>
                    </div>
                    <span v-if="licenses.some((license) => license.admin.id == user.id) || warningLicenses.length > 0" class="text px-1 flex flex-column align-items-center text-center gap-1">
                        <span>You are about to delete your account.</span>
                        <span v-if="licenses.some((license) => license.admin.id == user.id)">All the licenses you own will get removed from the 'Softron License Manager'. They will be available for others who know the serial to claim and become the owner of it.</span>
                        <span v-if="warningLicenses.length==1">One of your licenses has additional authorized users (see below). Note that those users will lose the ability to activate this license in the future.</span>
                        <span v-else-if="warningLicenses.length>1">Some of your licenses have additional authorized users (see below). Note that those users will lose their ability to activate those licenses in the future.</span>
                    </span>

                    <div v-if="warningLicenses.length>0" class="content flex flex-column w-full justify-content-left mt-3">
                        <DataTable :value="warningLicenses" size="small" tableStyle="min-width: 100%" :row-class="() => 'row'" class="mb-2" style="max-height: 30vh;" scrollable scrollHeight="flex" >
                            <Column field="serial_number" header="Serial number">
                                <template #body="slotProps">
                                    <span class="text-serial-number">{{ slotProps.data.serial_number }}</span>
                                </template>
                            </Column>
                            <Column field="product.application" header="Application" style="width: 25%">
                                <template #body="slotProps">
                                    <span>{{ isWebView && applicationGlobalInfo.name ?  applicationGlobalInfo.name : slotProps.data.product.application ?? "/" }}{{ slotProps.data.current_status.application_instance_id && slotProps.data.current_status.application_instance_id>0 ? ` ${slotProps.data.current_status.application_instance_id}` : "" }}</span>
                                </template>
                            </Column>
                            <Column field="admin" style="width: 15%">
                                <template #header>
                                    <div class="w-full flex flex-row align-items-center justify-content-center">
                                        <span class="p-column-title">Owner</span>
                                    </div>
                                </template>
                                <template #body="slotProps">
                                    <div class="w-full flex flex-row align-items-center justify-content-center">
                                        <Avatar v-if="user.id===slotProps.data.admin.id" class="avatar" :class="{ 'is-active-user': ((slotProps.data.current_status.activeUser) && (slotProps.data.admin.id === slotProps.data.current_status.activeUser.id)), 'is-user': user.id===slotProps.data.admin.id }" size="small" shape="circle" v-tooltip.top="{ value: getTooltipValueForUser(slotProps.data.admin, slotProps.data.current_status.activeUser) }">
                                            <SvgUserMe className="avatar-me-svg" :color="((slotProps.data.current_status.activeUser) && (slotProps.data.admin.id === slotProps.data.current_status.activeUser.id)) ? 'var(--avatar-inuse-font-color)' : 'var(--theme-font-color)'"/>
                                        </Avatar>
                                        <Avatar v-else class="avatar" :class="{ 'is-active-user': ((slotProps.data.current_status.activeUser) && (slotProps.data.admin.id === slotProps.data.current_status.activeUser.id)), 'is-user': user.id===slotProps.data.admin.id }" :label="getAvatarLabelFromFirstNameAndLastName(slotProps.data.admin.first_name, slotProps.data.admin.last_name)" size="small" shape="circle" v-tooltip.top="{ value: getTooltipValueForUser(slotProps.data.admin, slotProps.data.current_status.activeUser) }"/>
                                    </div>
                                </template>
                            </Column>
                            <Column field="delegates" style="width: 25%; min-width: 3rem; max-width: 16rem" class="resize">
                                <template #header>
                                    <div class="w-full flex flex-row align-items-center justify-content-center">
                                        <span class="p-column-title">Users</span>
                                    </div>
                                </template>
                                <template #body="slotProps">
                                    <div class="mt-2 relative w-full" style="height: 2.4rem;">
                                        <ScrollDelegatesAvatars :delegates="slotProps.data.delegates"/>
                                    </div>
                                </template>
                            </Column>
                        </DataTable>
                    </div>
                    
                    <div class="flex flex-column text" :class="{ 'mt-4': (licenses.some((license) => license.admin.id == user.id) || warningLicenses.length > 0) }">
                        <span class="text-center px-2 sm:px-7">Are you sure you want to delete your account ?</span>
                    </div>
                </div>

                <!-- Account CAN NOT be deleted -->
                <div v-else class="w-full flex flex-column align-items-center">
                    <div class="w-full flex justify-content-center align-items-center mb-3">
                        <SvgError className="popup-error-svg" color="var(--popup-error-color)"/>
                    </div>
                    <span class="text mb-3 px-1 flex flex-column align-items-center text-center gap-1">
                        <span>Your account can not be deleted.</span>
                        <span v-if="blockingActivations.length == 1">It appears there is a running activation that prevents the deletion of your account (see below).</span>
                        <span v-else>It appears there are running activations that prevent the deletion of your account (see below).</span>
                    </span>
                    <div v-if="blockingActivations.length>0" class="content flex flex-column w-full justify-content-left">
                        <DataTable :value="blockingActivations" size="small" tableStyle="min-width: 100%" :row-class="() => 'row'" class="mb-2" style="max-height: 30vh;" scrollable scrollHeight="flex" >
                            <Column field="serial_number" header="Serial number">
                                <template #body="slotProps">
                                    <span class="text-serial-number">{{ slotProps.data.serial_number }}</span>
                                </template>
                            </Column>
                            <Column field="product.application" header="Application" style="max-width: 30%">
                                <template #body="slotProps">
                                    <span>{{ isWebView && applicationGlobalInfo.name ?  applicationGlobalInfo.name : slotProps.data.product.application ?? "/" }}{{ slotProps.data.current_status.application_instance_id && slotProps.data.current_status.application_instance_id>0 ? ` ${slotProps.data.current_status.application_instance_id}` : "" }}</span>
                                </template>
                            </Column>
                            <Column field="admin" style="max-width: 30%">
                                <template #header>
                                    <div class="w-full flex flex-row align-items-center justify-content-center">
                                        <span class="p-column-title">Owner</span>
                                    </div>
                                </template>
                                <template #body="slotProps">
                                    <div class="w-full flex flex-row align-items-center justify-content-center">
                                        <Avatar v-if="user.id===slotProps.data.admin.id" class="avatar" :class="{ 'is-active-user': ((slotProps.data.current_status.activeUser) && (slotProps.data.admin.id === slotProps.data.current_status.activeUser.id)), 'is-user': user.id===slotProps.data.admin.id }" size="small" shape="circle" v-tooltip.top="{ value: getTooltipValueForUser(slotProps.data.admin, slotProps.data.current_status.activeUser) }">
                                            <SvgUserMe className="avatar-me-svg" :color="((slotProps.data.current_status.activeUser) && (slotProps.data.admin.id === slotProps.data.current_status.activeUser.id)) ? 'var(--avatar-inuse-font-color)' : 'var(--theme-font-color)'"/>
                                        </Avatar>
                                        <Avatar v-else class="avatar" :class="{ 'is-active-user': ((slotProps.data.current_status.activeUser) && (slotProps.data.admin.id === slotProps.data.current_status.activeUser.id)), 'is-user': user.id===slotProps.data.admin.id }" :label="getAvatarLabelFromFirstNameAndLastName(slotProps.data.admin.first_name, slotProps.data.admin.last_name)" size="small" shape="circle" v-tooltip.top="{ value: getTooltipValueForUser(slotProps.data.admin, slotProps.data.current_status.activeUser) }"/>
                                    </div>
                                </template>
                            </Column>
                            <Column>
                                <template #header>
                                    <div class="w-full flex flex-row align-items-center justify-content-center">
                                        <span class="p-column-title">Status</span>
                                    </div>
                                </template>
                                <template #body="slotProps">
                                    <div class="flex flex-row justify-content-center align-items-center">
                                        <StatusTag :statusInformation="slotProps.data.current_status" :pending="isPendingNow(slotProps.data) ? slotProps.data.pending : null" tooltipPosition="left" tagSize="small" :applicationName="slotProps.data.product.application"/>
                                    </div>
                                </template>
                            </Column>
                            <Column header="" style="max-width: 1rem">
                                <template #body="slotProps">
                                    <div class="flex flex-row justify-content-end align-items-center">
                                        <span class="pi pi-info-circle" v-tooltip.left="{ value: getTooltipForBlockingActivation(slotProps.data.current_status, slotProps.data.admin)}"/>
                                    </div>
                                </template>
                            </Column>
                        </DataTable>
                    </div>
                    <div class="mt-4 flex flex-column text w-full align-items-center gap-2">
                        <span class="text-center px-2 sm:px-7">To complete the deletion process, please release {{ blockingActivations.length == 1 ? 'this activation' : 'those activations' }} first.</span>
                    </div>
                </div>
            </div>

            <div class="dialog-footer">
                <a  class="justify-self-start flex align-items-center justify-content-center" :href="deleteAccountZendeskLink" target="_blank" @click="clickedZendeskLink">
                    <Button rounded text style="border-radius: 980px; padding: 0;">
                        <SvgQuestionMarkCircle className="info-svg" color="var(--button-normal-color)"/>
                    </Button>
                </a>

                <div class="buttons">
                    <ButtonModule type="secondary" severity="normal" label="Cancel" @click="$emit('close')"/>
                    <ButtonModule type="primary" severity="danger" label="Delete Account" @click="showPasswordVerificationDialog = true" :disabled="!accountCanBeDeleted"/>
                </div>
            </div>
        </template>
    </Dialog>

    <!-- DIALOGS -->

    <!-- Authentication using PASSWORD -->
    <VerificationAuthenticationDialog :showDialog="showPasswordVerificationDialog" :incorrectPassword="wrongPassword" @cancel="showPasswordVerificationDialog = false;" @ok="(val) => verificationAuthenticationDone(val)" :isLoading="httpDeleteAccountLoading"/>

</template>

<script setup>
// --- IMPORT ---
import { ref, toRefs, computed, watchEffect } from 'vue';
// PrimeVue
import Dialog from 'primevue/dialog';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Avatar from 'primevue/avatar'
// vue
import ButtonModule from '@/components/Modules/ButtonModule.vue'
import VerificationAuthenticationDialog from '@/components/Dialogs/OtherDialogs/VerificationAuthenticationDialog.vue';
import SvgWarning from '@/components/SVGs/SvgWarning.vue';
import SvgError from '@/components/SVGs/SvgError.vue';
import SvgUserMe from '@/components/SVGs/SvgUserMe.vue';
import ScrollDelegatesAvatars from '@/components/Licenses/Elements/ScrollDelegatesAvatars.vue';
import SvgQuestionMarkCircle from '@/components/SVGs/SvgQuestionMarkCircle.vue';
import StatusTag from '@/components/Licenses/Elements/StatusTag.vue'
// Others
import toastService from '@/composables/toastService';
import UserUtils from '@/composables/UserUtils';
import LicensesUtils from '@/composables/LicensesUtils';
import AvatarsUtils from '@/composables/AvatarsUtils'
import { useStore } from 'vuex';
import { useRouter } from 'vue-router'

const router = useRouter()
const store = useStore()
const { logoutUser, userAccountCanBeDeleted } = UserUtils(store, router)
const { isPendingNow } = LicensesUtils(store)
const { getAvatarLabelFromFirstNameAndLastName, getTooltipValueForUser } = AvatarsUtils()

// --- isWebView info ---
const isWebView = computed(() => store.state.applicationWebView.isWebView)
const applicationGlobalInfo = computed(() => store.state.applicationWebView.applicationGlobalInfo)
const user = computed(() => store.state.user.user)
const licenses = computed(() => store.state.licenses.licenses)

// API
import useApiRequest from '@/composables/HTTPS/useApiRequest';
const { fetchDeleteAccount } = useApiRequest()
const httpDeleteAccountLoading = ref(false)
const httpDeleteAccountData = ref(null)

// --- PROPS ---
const props = defineProps({
    showDialog: Boolean
})
const { showDialog } = toRefs(props)

// --- EMITS ---
const emit = defineEmits(['close'])

// --- CHECK IF DELETION IS POSSIBLE !!! ---
const blockingActivations = ref(null); // This array contains the licenses that makes the deletion impossible
const warningLicenses = ref(null); // This array contains the licenses (for which I am owner) that will be removed and has delegates
const accountCanBeDeleted = ref(false);
watchEffect(() => {
    if (showDialog.value) {
        accountCanBeDeleted.value = userAccountCanBeDeleted()
        if (accountCanBeDeleted.value) {
            warningLicenses.value = licenses.value.filter((license) => {
                return (license.admin.id == user.value.id) && (license.delegates.length>0)
            })
        } else {
            blockingActivations.value = licenses.value.filter((license) => {
                return license.current_status.activated && license.current_status.activeUser && ((license.admin.id == user.value.id) || (license.current_status.activeUser.id == user.value.id))
            })
        }
    }
})

// - DELETION IS NOT POSSIBLE -
function getTooltipForBlockingActivation(currentStatus, admin) {
    const isAdmin = (user.value.id == admin.id)
    const isActiveUser = (user.value.id == currentStatus.activeUser.id)
    // Case 1:  Not admin but active user
    if (!isAdmin && isActiveUser) {
        return `You are currently running this activation with your account. You must release this activation if you want to proceed the deletion of your account.`
    }
    // Case 2: Admin and not active user
    else if (isAdmin && !isActiveUser) {
        return `You are the current owner of this license. Deleting your account implies removing this license from the 'Softron License Manager' which is not possible if the license is currently in use. You must release this activation if you want to proceed the deletion of your account.`
    }
    // Case 3: Admin and active user
    else if (isAdmin && isActiveUser) {
        return `You are the current owner of this license and are currently running this activation with your account. Deleting your account implies removing this license from the 'Softron License Manager' which is not possible if the license is currently in use. You must release this activation if you want to proceed the deletion of your account.`
    }
    // Case 4: Not admin and not active user (should not be possible ...)
    return `You must release this activation if you want to proceed the deletion of your account.`
}

// --- REASON ---
const reason = ref('')
const reasonError = ref(null)

// Whenever the dialog opens, it should reset the input reason
watchEffect(() => {
    if (showDialog.value) { reason.value = '' }
})

// --- Support link ---
const deleteAccountZendeskLink = "https://softron.zendesk.com/hc/en-us/sections/14666349935516-Softron-License-Manager-New-2024-Activations"
const clickedZendeskLink = () => {
    // (in case it is in the web view in the app, ask the app to open it also, because the href thing will not work if it is in the web view)
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.jsOpenWebPage) {
        window.webkit.messageHandlers.jsOpenWebPage.postMessage(deleteAccountZendeskLink);
    }
}

// --- PASSWORD VERIFICATION ---
const showPasswordVerificationDialog = ref(false)
const wrongPassword = ref(false)
// Whenever the dialog opens, it should reset the wrongPassword boolean
watchEffect(() => {
    if (showPasswordVerificationDialog.value) { wrongPassword.value = false }
})
const verificationAuthenticationDone = (password) => {
    // IT CALLS THE SUBMIT REQUEST WITH THE PASSWORD AS THE VERIFICATION
    submitDeleteAccountConfirmed(password)
}

// --- SUBMIT DELETE ACCOUNT CONFIRMED ---
const submitDeleteAccountConfirmed = async (passwordVerification) => {
    wrongPassword.value = false
    // Send HTTP request with passwordVerification
    httpDeleteAccountLoading.value = true
    try {
        httpDeleteAccountData.value = await fetchDeleteAccount({ password_plain_auth: passwordVerification, reason: reason.value })
        // Reset of values
        showPasswordVerificationDialog.value = false
        wrongPassword.value = false
        // Delete the account info from the cache
        logoutUser()
        // Success toast
        toastService.add({ severity: 'success', summary: "Delete Account: Success", detail: 'The account has been successfully deleted.', life: 5000 });
        // Close
        emit('close')
    } catch (err) {
        handleDeleteAccountError(err)
    } finally {
        httpDeleteAccountLoading.value = false
    }
}
// Handle an error from the server
const handleDeleteAccountError = (error) => {
    try {
        if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
            toastService.add({ severity: 'error', summary: `Delete Account: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
            return
        }
        const errorStatus = error.response.status
        const errorCode = error.response.data.error_code
        const errorMessage = error.response.data.error_message
        switch (errorStatus) {
            case 400: // ACCESS_TOKEN_MISSING / MISSING_OBJ / MISSING_OBJ_ELEM (400)
                if (errorCode == 'ACCESS_TOKEN_MISSING') {
                    toastService.add({ severity: 'error', summary: "Delete Account: Authorization Issue.", detail: 'You are not logged in.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else if (errorCode == 'MISSING_OBJ' || errorCode == 'MISSING_OBJ_ELEM') { // MISSING_OBJ / MISSING_OBJ_ELEM
                    toastService.add({ severity: 'warn', summary: "Delete Account: Missing Information.", detail: 'It seems that the request body does not contain the necessary information.', life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `Delete Account: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 401: // WRONG_PASSWORD / ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID / ACCOUNT_NOT_DELETABLE (401)
                if (errorCode == 'WRONG_PASSWORD') { // WRONG_PASSWORD
                    wrongPassword.value = true
                }
                else if (errorCode == 'ACCOUNT_NOT_DELETABLE') { // ACCOUNT_NOT_DELETABLE
                    toastService.add({ severity: 'warn', summary: "Delete Account: Not Authorized.", detail: errorMessage, life: 5000 });
                    showPasswordVerificationDialog.value = false
                }
                else if (errorCode == 'ACCESS_TOKEN_EXPIRED' || errorCode == 'ACCESS_TOKEN_INVALID') { // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID
                    // Logout: Do nothing, the interceptor should have made a refresh token request!
                    // logoutUser()
                }
                else {  toastService.add({ severity: 'error', summary: `Delete Account: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 404: // ACCOUNT_NOT_FOUND (404)
                if (errorCode == 'ACCOUNT_NOT_FOUND' ) { // ACCOUNT_NOT_FOUND => toast + logout
                    toastService.add({ severity: 'error', summary: "Delete Account: Account Not Found.", detail: 'The account no more exists.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else {  toastService.add({ severity: 'error', summary: `Delete Account: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 500: // DATABASE_ERROR / INTERNAL_ERROR (500) => toast
                if (errorCode == 'DATABASE_ERROR' || errorCode == 'INTERNAL_ERROR') {
                    toastService.add({ severity: 'error', summary: "Delete Account: Internal Error.", detail: 'Sorry, it seems that there is an internal error. Try later.', life: 5000 });
                    showPasswordVerificationDialog.value = false
                }
                else {  toastService.add({ severity: 'error', summary: `Delete Account: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            default:
                toastService.add({ severity: 'error', summary: `Delete Account: Error ${errorStatus}.`, detail: errorMessage, life: 12000 });
                break;
        }
    } catch {
        toastService.add({ severity: 'error', summary: `Delete Account: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}
</script>

<style scoped>
</style>